import { motion } from "framer-motion";
import { Mail, Phone, GraduationCap } from "lucide-react";

const facultyMembers = [
  {
    name: "Dr. Rajesh Kumar",
    role: "Principal",
    subject: "Administration",
    qualification: "Ph.D. in Education",
    experience: "25+ years",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80",
    bio: "Dr. Kumar brings over two decades of experience in education leadership and is committed to excellence in learning.",
  },
  {
    name: "Mrs. Priya Sharma",
    role: "Vice Principal",
    subject: "English",
    qualification: "M.A. English, B.Ed",
    experience: "18 years",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80",
    bio: "A passionate educator who believes in making literature accessible and enjoyable for all students.",
  },
  {
    name: "Mr. Amit Verma",
    role: "Head of Science",
    subject: "Physics",
    qualification: "M.Sc Physics, B.Ed",
    experience: "15 years",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=400&q=80",
    bio: "Known for making complex physics concepts simple through practical demonstrations and experiments.",
  },
  {
    name: "Mrs. Sunita Reddy",
    role: "Senior Teacher",
    subject: "Mathematics",
    qualification: "M.Sc Mathematics, B.Ed",
    experience: "12 years",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&q=80",
    bio: "Specializes in building strong mathematical foundations and problem-solving skills in students.",
  },
  {
    name: "Mr. Vikram Singh",
    role: "Senior Teacher",
    subject: "Chemistry",
    qualification: "M.Sc Chemistry, B.Ed",
    experience: "10 years",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=400&q=80",
    bio: "Brings chemistry to life with engaging experiments and real-world applications.",
  },
  {
    name: "Mrs. Anita Mehta",
    role: "Senior Teacher",
    subject: "Biology",
    qualification: "M.Sc Biology, B.Ed",
    experience: "11 years",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=400&q=80",
    bio: "Passionate about environmental science and inspiring the next generation of biologists.",
  },
  {
    name: "Mr. Rahul Gupta",
    role: "Teacher",
    subject: "Computer Science",
    qualification: "MCA, B.Ed",
    experience: "8 years",
    image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=400&q=80",
    bio: "Technology enthusiast who teaches coding and digital skills in an engaging manner.",
  },
  {
    name: "Mrs. Kavita Joshi",
    role: "Teacher",
    subject: "Hindi",
    qualification: "M.A. Hindi, B.Ed",
    experience: "9 years",
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=400&q=80",
    bio: "Dedicated to preserving and promoting Hindi language and literature among students.",
  },
  {
    name: "Mr. Sanjay Patel",
    role: "Teacher",
    subject: "Social Science",
    qualification: "M.A. History, B.Ed",
    experience: "7 years",
    image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=400&q=80",
    bio: "Makes history and geography come alive through storytelling and interactive sessions.",
  },
  {
    name: "Mrs. Deepa Nair",
    role: "Pre-Primary Head",
    subject: "Early Childhood",
    qualification: "B.A., NTT",
    experience: "14 years",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=400&q=80",
    bio: "Expert in early childhood development with a caring and nurturing approach to teaching.",
  },
  {
    name: "Mr. Arun Kumar",
    role: "Sports Coach",
    subject: "Physical Education",
    qualification: "M.P.Ed",
    experience: "12 years",
    image: "https://images.unsplash.com/photo-1507591064344-4c6ce005b128?auto=format&fit=crop&w=400&q=80",
    bio: "Former state-level athlete committed to developing sportsmanship and fitness in students.",
  },
  {
    name: "Mrs. Lakshmi Iyer",
    role: "Music Teacher",
    subject: "Music",
    qualification: "M.A. Music",
    experience: "10 years",
    image: "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?auto=format&fit=crop&w=400&q=80",
    bio: "Classically trained musician who nurtures musical talent and appreciation in students.",
  },
];

const Faculty = () => {
  return (
    <div data-testid="faculty-page">
      {/* Page Header */}
      <section className="bg-primary py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Our Faculty
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Meet our dedicated team of educators committed to shaping young minds
            </p>
          </motion.div>
        </div>
      </section>

      {/* Faculty Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-secondary font-medium text-sm uppercase tracking-wider">
              Expert Educators
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
              Meet Our Teachers
            </h2>
            <div className="section-divider mx-auto"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Our faculty comprises experienced and passionate educators who are dedicated 
              to nurturing the potential of every student.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {facultyMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h3 className="font-heading text-lg font-semibold">{member.name}</h3>
                    <p className="text-sm text-white/80">{member.role}</p>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <GraduationCap size={16} className="text-primary" />
                    <span className="text-sm font-medium text-primary">{member.subject}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {member.qualification} • {member.experience}
                  </p>
                  <p className="text-sm text-gray-600 line-clamp-2">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our Team */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-4">
              Join Our Team
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Are you passionate about education? We're always looking for talented 
              educators to join our team. Send us your resume!
            </p>
            <a 
              href="mailto:careers@id4uschool.com"
              className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
            >
              <Mail size={18} />
              careers@id4uschool.com
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Faculty;
